import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsChristmasBonus } from "./columns";
import { useMemo, useRef, useState } from "react";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import { PaginatedChristmasBonusQueryVariables, PaginatedChristmasBonusDocument, ChristmasBonusEdge, PaginatedChristmasBonusQuery, ChristmasBonus } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { useRemoveChristmasBonusMutationHook } from "@/hooks/mutations";
import { Button } from "@/components/ui/button";
import ChristmasBonusFilter from "./ChristmasBonusFilter";
import AddChristmasBonusDialog from "./AddChristmasBonusDialog";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedChristmasBonusQueryVariables>) => void;
}

/**
 * ChristmasBonusComponent component for displaying the christmas bonus table
 * @returns JSX element
 */
function ChristmasBonusComponent() {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeChristmasBonusMutation } = useRemoveChristmasBonusMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedChristmasBonus";

  // State management
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveChristmasBonusOpen, setIsRemoveChristmasBonusOpen] = useState(false);
  const [selectedChristmasBonus, setSelectedChristmasBonus] = useState<ChristmasBonus | null>(null);

  const onFilterSubmit = (variables: PaginatedChristmasBonusQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (christmasBonus: ChristmasBonus) => {
    setSelectedChristmasBonus(christmasBonus);
    setIsRemoveChristmasBonusOpen(true);
  };

  const handleRemoveChristmasBonus = (christmasBonus: ChristmasBonus) => {
    removeChristmasBonusMutation({ variables: { removeChristmasBonusId: christmasBonus.id } }).finally(() => {
      setIsRemoveChristmasBonusOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsChristmasBonus(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<ChristmasBonusEdge, PaginatedChristmasBonusQuery, PaginatedChristmasBonusQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedChristmasBonusDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <ChristmasBonusFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddModalOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>


      <AddChristmasBonusDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />

      {/* Remove Christmas Bonus Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveChristmasBonusOpen}
        onOpenChange={setIsRemoveChristmasBonusOpen}
        actionButtonFn={() => handleRemoveChristmasBonus(selectedChristmasBonus as ChristmasBonus)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removechristmasbonus")}
      />
    </>
  );
}

export default ChristmasBonusComponent;
